import { useEffect, useRef, useState } from 'react';
import { CellStyle, ColDef, GridApi } from 'ag-grid-community';
import { IVirtualMachine } from '@vivli/features/virtual-machine/infrastructure/interface';
import { first } from 'rxjs/operators';
import { useModalApiContext, useVirtualMachineApiContext } from '@vivli/core/infrastructure/context';
import { ManageVmModalComponent } from '@vivli/features/virtual-machine/components';
import { GridPageView, LoadIndicatorCenteredView } from '@vivli/shared/views';

const centeredStyle: CellStyle = {
    textAlign: 'center'
};

export const ManageVmsFeature = () => {
    const [vms, setVms] = useState<IVirtualMachine[]>(null);
    const [isLoading, setIsLoading] = useState(false);
    const gridRef = useRef<GridApi>();
    const virtualMachineService = useVirtualMachineApiContext();
    const modalService = useModalApiContext();

    const columnDefs: ColDef[] = [
        {
            field: 'dataRequestDoi',
            headerName: 'Data Request Id',
            sortable: true,
            filter: true,
            initialSort: 'asc',
            cellStyle: centeredStyle
        },
        {
            field: 'dataRequestTitle',
            headerName: 'Title',
            sortable: true,
            filter: true
        },
        {
            field: 'provisionedByEmail',
            headerName: 'Requester Email',
            sortable: true,
            filter: true
        },
        {
            field: 'status',
            headerName: 'Status',
            sortable: true,
            filter: true,
            cellStyle: centeredStyle
        },
        {
            field: 'daysSinceLastLogin',
            headerName: 'Days Since Last Login',
            sortable: true,
            filter: false,
            cellStyle: centeredStyle
        },
        {
            field: 'daysSinceLastStart',
            headerName: 'Runtime (Days)',
            sortable: true,
            filter: false,
            cellStyle: centeredStyle
        }
    ];

    const handleRowClick = (data) => {
        const modalId = modalService.custom(
            <ManageVmModalComponent
                vmId={data.id}
                onCancel={() => {
                    modalService.dismiss(modalId);
                }}
            />,
            { style: { minWidth: 1000 } }
        );
    };

    const handleError = (e) => {
        modalService.error(e);
        setIsLoading(false);
    };

    const getVms = () => {
        setIsLoading(true);
        virtualMachineService
            .getVms(false)
            .pipe(first())
            .subscribe((vms) => {
                setIsLoading(false);
                setVms(vms);
            }, handleError);
    };

    useEffect(() => {
        getVms();
    }, []);

    if (!vms) {
        return <LoadIndicatorCenteredView />;
    }

    return (
        <GridPageView
            title="Secure Research Environments"
            subHeader="Secure Research environments used to analyze data from approved data requests with studies from my organization"
            columnDefs={columnDefs}
            rowData={vms}
            onRowClick={handleRowClick}
            exportTitle="Secure Research Environment Management"
            asyncUpdateKey="id"
            dataRefreshing={isLoading}
            onGridReady={(gridApi) => (gridRef.current = gridApi)}
            fullWidthColumns={true}
        />
    );
};
